import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad2 = (n) => String(n).padStart(2, '0');

const toISODate = (year, month, day) => `${year}-${pad2(month + 1)}-${pad2(day)}`;

const toDisplayDate = (val) => {
  if (!val) return '';
  const parts = val.split('T')[0].split('-');
  if (parts.length !== 3) return val;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const HolidayDatePicker = ({
  id,
  label,
  value,
  onChange,
  min,
  max,
  holidays = [],
  appliedLeaves = [],
  error = false,
  helperText = '',
  required = false,
  fullWidth = true,
  disabled = false,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);

  // Parse initial year & month based on current value or today
  const initialDate = useMemo(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }
    return new Date();
  }, [value]);

  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());

  // Quick lookup map for holidays: 'YYYY-MM-DD' -> holiday object
  const holidayMap = useMemo(() => {
    const map = {};
    if (Array.isArray(holidays)) {
      holidays.forEach((h) => {
        if (h && h.holidayDate) {
          const dateStr = h.holidayDate.split('T')[0];
          map[dateStr] = h;
        }
      });
    }
    return map;
  }, [holidays]);

  // Quick lookup map for user's applied leaves: 'YYYY-MM-DD' -> leave object
  const appliedLeaveMap = useMemo(() => {
    const map = {};
    if (Array.isArray(appliedLeaves)) {
      appliedLeaves.forEach((lv) => {
        if (lv && lv.startDate && lv.endDate) {
          const status = lv.status;
          if (status === 'APPROVED' || status === 'PENDING' || status === 'CANCELLATION_REQUESTED') {
            const startStr = lv.startDate.split('T')[0];
            const endStr = lv.endDate.split('T')[0];
            const s = new Date(startStr);
            const e = new Date(endStr);
            const cur = new Date(s);
            while (cur <= e) {
              const dStr = cur.toISOString().split('T')[0];
              map[dStr] = lv;
              cur.setDate(cur.getDate() + 1);
            }
          }
        }
      });
    }
    return map;
  }, [appliedLeaves]);

  // Open Popover
  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        setCurrentYear(Number(parts[0]));
        setCurrentMonth(Number(parts[1]) - 1);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Month navigation
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  // Calendar cells generation (dynamic rows: 5 or 6 as needed)
  const calendarDays = useMemo(() => {
    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    const days = [];

    // Leading days from previous month
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      const dateStr = toISODate(prevYear, prevMonth, dayNum);
      days.push({
        dayNum,
        dateStr,
        isCurrentMonth: false,
      });
    }

    // Days of current month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toISODate(currentYear, currentMonth, d);
      days.push({
        dayNum: d,
        dateStr,
        isCurrentMonth: true,
      });
    }

    // Trailing days to fill only active weeks (35 or 42 only if needed)
    const totalNeeded = Math.ceil(days.length / 7) * 7;
    const remaining = totalNeeded - days.length;
    for (let i = 1; i <= remaining; i++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      const dateStr = toISODate(nextYear, nextMonth, i);
      days.push({
        dayNum: i,
        dateStr,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  const todayStr = useMemo(() => {
    const now = new Date();
    return toISODate(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const handleSelectDate = (dateStr) => {
    if (onChange) {
      onChange(dateStr);
    }
    handleClose();
  };

  const handleSelectToday = () => {
    if (onChange) {
      onChange(todayStr);
    }
    handleClose();
  };

  const monthName = new Date(currentYear, currentMonth, 1).toLocaleString('en-US', {
    month: 'short',
  });

  const selectedHoliday = value ? holidayMap[value] : null;
  const selectedLeave = value ? appliedLeaveMap[value] : null;

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        id={id}
        ref={inputRef}
        label={label}
        value={value ? toDisplayDate(value) : ''}
        onClick={handleOpen}
        placeholder="DD/MM/YYYY"
        error={error}
        helperText={helperText}
        required={required}
        fullWidth={fullWidth}
        disabled={disabled}
        size={size}
        slotProps={{
          formHelperText: {
            sx: { fontWeight: 500 },
          },
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={handleOpen}
                  disabled={disabled}
                  sx={{
                    color: selectedHoliday
                      ? '#059669'
                      : selectedLeave
                      ? '#d97706'
                      : 'var(--primary, #045864)',
                    p: 0.5,
                  }}
                >
                  {selectedHoliday ? (
                    <CelebrationRoundedIcon sx={{ fontSize: 18 }} />
                  ) : selectedLeave ? (
                    <EventAvailableRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-root': { cursor: disabled ? 'default' : 'pointer' },
          '& .MuiInputBase-input': { cursor: disabled ? 'default' : 'pointer' },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 1.25,
              borderRadius: '12px',
              boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.12), 0 2px 6px 0 rgba(0, 0, 0, 0.04)',
              border: '1px solid #e2e8f0',
              width: 250,
            },
          },
        }}
      >
        {/* Header with Month / Year Navigation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.75,
            px: 0.5,
          }}
        >
          <IconButton size="small" onClick={handlePrevMonth} sx={{ p: 0.25 }}>
            <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.82rem' }}>
            {monthName} {currentYear}
          </Typography>

          <IconButton size="small" onClick={handleNextMonth} sx={{ p: 0.25 }}>
            <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Week Days Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
            textAlign: 'center',
            mb: 0.5,
          }}
        >
          {WEEK_DAYS.map((wd, idx) => (
            <Typography
              key={wd}
              variant="caption"
              sx={{
                fontWeight: 700,
                color: idx === 0 || idx === 6 ? '#94a3b8' : '#64748b',
                fontSize: '0.65rem',
              }}
            >
              {wd}
            </Typography>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
          }}
        >
          {calendarDays.map(({ dayNum, dateStr, isCurrentMonth }) => {
            const isSelected = value === dateStr;
            const isToday = todayStr === dateStr;
            const holiday = holidayMap[dateStr];
            const isHoliday = Boolean(holiday);
            const appliedLeave = appliedLeaveMap[dateStr];
            const isAppliedLeave = Boolean(appliedLeave);

            // Min / Max disabled check
            const isDisabled =
              !isCurrentMonth ||
              (min && dateStr < min) ||
              (max && dateStr > max);

            const isApprovedLeave = appliedLeave?.status === 'APPROVED';

            const dayButton = (
              <Button
                key={dateStr}
                disabled={isDisabled}
                onClick={() => handleSelectDate(dateStr)}
                sx={{
                  minWidth: 0,
                  width: 28,
                  height: 28,
                  p: 0,
                  m: 'auto',
                  borderRadius: '6px',
                  position: 'relative',
                  fontWeight: isSelected || isHoliday || isAppliedLeave ? 700 : 500,
                  fontSize: '0.72rem',
                  color: isSelected
                    ? '#ffffff !important'
                    : isHoliday
                    ? '#059669'
                    : isAppliedLeave
                    ? isApprovedLeave
                      ? '#0284c7'
                      : '#d97706'
                    : !isCurrentMonth
                    ? '#cbd5e1'
                    : '#1e293b',
                  backgroundColor: isSelected
                    ? 'var(--primary, #045864) !important'
                    : isHoliday && isCurrentMonth
                    ? 'rgba(16, 185, 129, 0.14)'
                    : isAppliedLeave && isCurrentMonth
                    ? isApprovedLeave
                      ? 'rgba(2, 132, 199, 0.12)'
                      : 'rgba(245, 158, 11, 0.14)'
                    : 'transparent',
                  border: isToday && !isSelected
                    ? '1.2px solid var(--primary, #045864)'
                    : isHoliday && !isSelected && isCurrentMonth
                    ? '1px dashed #10b981'
                    : isAppliedLeave && !isSelected && isCurrentMonth
                    ? isApprovedLeave
                      ? '1px solid #38bdf8'
                      : '1px dashed #f59e0b'
                    : 'none',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? 'var(--primary, #045864)'
                      : isHoliday
                      ? 'rgba(16, 185, 129, 0.25)'
                      : isAppliedLeave
                      ? isApprovedLeave
                        ? 'rgba(2, 132, 199, 0.22)'
                        : 'rgba(245, 158, 11, 0.25)'
                      : '#f1f5f9',
                  },
                  '&.Mui-disabled': {
                    color: '#e2e8f0',
                  },
                }}
              >
                {dayNum}
                {/* Indicator Dot */}
                {isCurrentMonth && (isHoliday || isAppliedLeave) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      backgroundColor: isSelected
                        ? '#ffffff'
                        : isHoliday
                        ? '#10b981'
                        : isApprovedLeave
                        ? '#0284c7'
                        : '#f59e0b',
                    }}
                  />
                )}
              </Button>
            );

            // Tooltip generation
            if (isCurrentMonth && (isHoliday || isAppliedLeave)) {
              let tooltipTitle = '';
              if (isHoliday && isAppliedLeave) {
                const leaveTypeLabel = appliedLeave.leaveType === 'FULL_DAY' ? 'Full Day' : appliedLeave.leaveType === 'FIRST_HALF' ? 'First Half' : 'Second Half';
                tooltipTitle = `🎉 ${holiday.name} (Holiday) · ${appliedLeave.status === 'APPROVED' ? 'Approved' : 'Pending'} Leave (${leaveTypeLabel})`;
              } else if (isHoliday) {
                tooltipTitle = `🎉 ${holiday.name} (Holiday)`;
              } else if (isAppliedLeave) {
                const leaveTypeLabel = appliedLeave.leaveType === 'FULL_DAY' ? 'Full Day' : appliedLeave.leaveType === 'FIRST_HALF' ? 'First Half' : 'Second Half';
                tooltipTitle = `${appliedLeave.status === 'APPROVED' ? '🗓️ Approved' : '⏳ Pending'} Leave (${leaveTypeLabel})`;
              }

              return (
                <Tooltip
                  key={dateStr}
                  title={tooltipTitle}
                  arrow
                  placement="top"
                >
                  <Box sx={{ display: 'inline-flex', m: 'auto' }}>{dayButton}</Box>
                </Tooltip>
              );
            }

            return dayButton;
          })}
        </Box>

        {/* Footer info & quick actions */}
        <Box
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.5,
          }}
        >
          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                }}
              />
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, fontSize: '0.66rem' }}>
                Holiday
              </Typography>
            </Box>

            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                }}
              />
              <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600, fontSize: '0.66rem' }}>
                Leave
              </Typography>
            </Box>
          </Box>

          <Button
            size="small"
            onClick={handleSelectToday}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.7rem',
              p: '2px 6px',
              minWidth: 0,
              color: 'var(--primary, #045864)',
            }}
          >
            Today
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default HolidayDatePicker;
